/**
 * @file redirectBreaker.js
 * @description Notbremse (Circuit Breaker) gegen automatische Weiterleitungsschleifen.
 *
 * Funktionsweise:
 * - Protokolliert Redirects im `sessionStorage`.
 * - Löst aus, sobald innerhalb von 5 Sekunden (5000 ms) mehr als 3 Weiterleitungen stattfinden.
 * - Unterbricht die Schleife hart und ermöglicht das Rendern einer sauberen Notfallseite,
 *   statt den Tab einfrieren oder abstürzen zu lassen.
 */

const STORAGE_KEY = 'thekeos_redirect_breaker_v1';
export const REDIRECT_WINDOW_MS = 5000;
export const MAX_REDIRECT_COUNT = 3;

/**
 * Liest den aktuellen Breaker-Zustand aus dem sessionStorage.
 * @returns {{ count: number, startTime: number }}
 */
function getBreakerState() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { count: 0, startTime: 0 };
    const parsed = JSON.parse(raw);
    return {
      count: Number(parsed.count) || 0,
      startTime: Number(parsed.startTime) || 0,
    };
  } catch {
    return { count: 0, startTime: 0 };
  }
}

/**
 * Speichert den Breaker-Zustand im sessionStorage.
 * @param {{ count: number, startTime: number }} state
 */
function setBreakerState(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Fehler bei deaktiviertem Storage im privaten Modus ignorieren
  }
}

/**
 * Registriert eine automatische Weiterleitung und prüft, ob die Schleifen-Schwelle überschritten wurde.
 * @returns {{ tripped: boolean, count: number }}
 */
export function recordRedirect() {
  const now = Date.now();
  const state = getBreakerState();

  let nextCount = 1;
  let nextStartTime = now;

  if (state.startTime > 0 && now - state.startTime <= REDIRECT_WINDOW_MS) {
    nextCount = state.count + 1;
    nextStartTime = state.startTime;
  }

  setBreakerState({ count: nextCount, startTime: nextStartTime });

  const tripped = nextCount >= MAX_REDIRECT_COUNT;
  return { tripped, count: nextCount };
}

/**
 * Prüft, ob die Notbremse aktuell ausgelöst ist.
 * @returns {boolean}
 */
export function isRedirectLoopTripped() {
  const now = Date.now();
  const state = getBreakerState();
  return state.count >= MAX_REDIRECT_COUNT && now - state.startTime <= REDIRECT_WINDOW_MS;
}

/**
 * Setzt den Weiterleitungszähler zurück (z. B. nach erfolgreicher Navigation oder manuellem Reset).
 */
export function resetRedirectBreaker() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignorieren
  }
}

/**
 * Bereinigt eine URL von fehlerhaften Legacy-Mustern (~and~, ?/ etc.).
 * @param {string} url - Roh-URL oder Pfad
 * @returns {string} Normalisierter Pfad
 */
export function sanitizeRedirectPath(url) {
  if (!url || typeof url !== 'string') return '/dashboard';

  let cleaned = url.trim();

  // Verhindert Open-Redirects: Darf nur relative App-Pfade enthalten
  if (!cleaned.startsWith('/') || cleaned.startsWith('//') || cleaned.startsWith('/\\')) {
    return '/dashboard';
  }

  // Entfernt historische GitHub-Pages-Muster
  cleaned = cleaned.replace(/~and~/g, '&');
  if (cleaned.startsWith('/?/')) {
    cleaned = cleaned.slice(2);
  }

  // Verhindert Selbst-Weiterleitungen auf Login
  if (cleaned === '/login' || cleaned.startsWith('/login?')) {
    return '/dashboard';
  }

  return cleaned;
}
